// Custom mapbox-gl-draw theme matching the app's light-blue palette
// (primary button #7EC6F3, map overlays #4eb3f2). This replaces the library's
// default orange/teal theme. It must cover every feature state (inactive /
// active / static, vertices, midpoints) so all drawing states keep rendering.

const COLOR_PRIMARY = "#4eb3f2"; // base line/fill — matches the map circle ring
const COLOR_ACTIVE = "#1f8fe0"; // deeper blue for the active / in-progress shape
const COLOR_VERTEX = "#ffffff"; // vertex halo
const COLOR_STATIC = "#9ecbe8"; // muted blue for non-interactive (static) mode

const FILL_OPACITY_INACTIVE = 0.16;
const FILL_OPACITY_ACTIVE = 0.22;

export const DRAW_STYLES = [
  // --- Polygon fill ---
  {
    id: "gl-draw-polygon-fill-inactive",
    type: "fill",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Polygon"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "fill-color": COLOR_PRIMARY,
      "fill-outline-color": COLOR_PRIMARY,
      "fill-opacity": FILL_OPACITY_INACTIVE,
    },
  },
  {
    id: "gl-draw-polygon-fill-active",
    type: "fill",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
    paint: {
      "fill-color": COLOR_ACTIVE,
      "fill-outline-color": COLOR_ACTIVE,
      "fill-opacity": FILL_OPACITY_ACTIVE,
    },
  },
  {
    id: "gl-draw-polygon-midpoint",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
    paint: {
      "circle-radius": 4,
      "circle-color": COLOR_ACTIVE,
    },
  },
  // --- Polygon stroke ---
  {
    id: "gl-draw-polygon-stroke-inactive",
    type: "line",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Polygon"],
      ["!=", "mode", "static"],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": COLOR_PRIMARY,
      "line-width": 2.5,
    },
  },
  {
    id: "gl-draw-polygon-stroke-active",
    type: "line",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": COLOR_ACTIVE,
      "line-dasharray": [0.2, 2],
      "line-width": 2.5,
    },
  },
  // --- Line strings (used while drawing) ---
  {
    id: "gl-draw-line-inactive",
    type: "line",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "LineString"],
      ["!=", "mode", "static"],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": COLOR_PRIMARY,
      "line-width": 2.5,
    },
  },
  {
    id: "gl-draw-line-active",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "active", "true"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": COLOR_ACTIVE,
      "line-dasharray": [0.2, 2],
      "line-width": 2.5,
    },
  },
  // --- Vertices (halo + center) ---
  {
    id: "gl-draw-polygon-and-line-vertex-stroke-inactive",
    type: "circle",
    filter: [
      "all",
      ["==", "meta", "vertex"],
      ["==", "$type", "Point"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "circle-radius": 6,
      "circle-color": COLOR_VERTEX,
    },
  },
  {
    id: "gl-draw-polygon-and-line-vertex-inactive",
    type: "circle",
    filter: [
      "all",
      ["==", "meta", "vertex"],
      ["==", "$type", "Point"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "circle-radius": 4,
      "circle-color": COLOR_ACTIVE,
    },
  },
  // --- Standalone points (kept for completeness) ---
  {
    id: "gl-draw-point-point-stroke-inactive",
    type: "circle",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Point"],
      ["==", "meta", "feature"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "circle-radius": 6,
      "circle-opacity": 1,
      "circle-color": COLOR_VERTEX,
    },
  },
  {
    id: "gl-draw-point-inactive",
    type: "circle",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Point"],
      ["==", "meta", "feature"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "circle-radius": 4,
      "circle-color": COLOR_PRIMARY,
    },
  },
  {
    id: "gl-draw-point-stroke-active",
    type: "circle",
    filter: [
      "all",
      ["==", "$type", "Point"],
      ["==", "active", "true"],
      ["!=", "meta", "midpoint"],
    ],
    paint: {
      "circle-radius": 8,
      "circle-color": COLOR_VERTEX,
    },
  },
  {
    id: "gl-draw-point-active",
    type: "circle",
    filter: [
      "all",
      ["==", "$type", "Point"],
      ["!=", "meta", "midpoint"],
      ["==", "active", "true"],
    ],
    paint: {
      "circle-radius": 6,
      "circle-color": COLOR_ACTIVE,
    },
  },
  // --- Static mode (non-interactive) ---
  {
    id: "gl-draw-polygon-fill-static",
    type: "fill",
    filter: ["all", ["==", "mode", "static"], ["==", "$type", "Polygon"]],
    paint: {
      "fill-color": COLOR_PRIMARY,
      "fill-outline-color": COLOR_PRIMARY,
      "fill-opacity": FILL_OPACITY_INACTIVE,
    },
  },
  {
    id: "gl-draw-polygon-stroke-static",
    type: "line",
    filter: ["all", ["==", "mode", "static"], ["==", "$type", "Polygon"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": COLOR_PRIMARY,
      "line-width": 2.5,
    },
  },
  {
    id: "gl-draw-line-static",
    type: "line",
    filter: ["all", ["==", "mode", "static"], ["==", "$type", "LineString"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": COLOR_STATIC,
      "line-width": 2.5,
    },
  },
  {
    id: "gl-draw-point-static",
    type: "circle",
    filter: ["all", ["==", "mode", "static"], ["==", "$type", "Point"]],
    paint: {
      "circle-radius": 4,
      "circle-color": COLOR_STATIC,
    },
  },
];
